import { Badge, Button, Card, EmptyState, Spinner } from '@ohear/ui'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect } from 'react'
import { ExportButtons, type ExportItem } from '../../../features/exports'
import { DownloadAllButton } from '../../../features/supervisor/DownloadAllButton'
import { StemRender } from '../../../features/supervisor/StemRender'
import { useProjectStems } from '../../../lib/api/stems'
import { useRequireRole } from '../../../lib/auth'
import { subscribeOrgChannel } from '../../../lib/realtime'

export const Route = createFileRoute('/supervisor/projects/$projectId/stems')({
  component: SupervisorStemsPortal,
})

function SupervisorStemsPortal() {
  useRequireRole(['supervisor'])
  const { projectId } = Route.useParams()
  const stems = useProjectStems(projectId)
  const qc = useQueryClient()

  // Realtime: subscribe to stem render events. The server emits inserts on
  // `stem_renders` rows when jobs start/complete; either way we just refetch.
  useEffect(() => {
    const orgId = (stems.data as { organization_id?: string } | undefined)?.organization_id
    if (!orgId) return undefined
    return subscribeOrgChannel(orgId, 'stem_renders', () => {
      void qc.invalidateQueries({ queryKey: ['supervisor', 'stems', projectId] })
    })
  }, [stems.data, projectId, qc])

  const characters = stems.data?.characters ?? []
  const readyCount = characters.filter((c) => c.status === 'ready').length

  const qcExportItems: ExportItem[] = characters
    .filter((c) => c.latest_version != null && c.latest_version > 0)
    .map((c) => ({
      label: `QC ${c.character_display_name} v${c.latest_version}`,
      format: 'pdf' as const,
      endpoint: '/api/v1/exports/qc-report',
      params: {
        character_id: c.character_id,
        version: c.latest_version as number,
      },
    }))

  return (
    <section className="flex flex-col gap-4" data-tour="stem-download">
      <header className="flex flex-wrap items-end justify-between gap-3 pb-4 border-b border-[var(--border)]">
        <div className="flex flex-col gap-1">
          <div className="text-[12px] uppercase tracking-wide text-[var(--text-3)]">Delivery</div>
          <h1 className="text-[22px] font-semibold tracking-tight m-0">Stems</h1>
          <p className="text-[13px] text-[var(--text-2)] m-0">
            <span className="tabular-nums font-medium text-[var(--text)]">{readyCount}</span> of{' '}
            <span className="tabular-nums">{characters.length}</span> ready to download.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {qcExportItems.length > 0 ? <ExportButtons exports={qcExportItems} /> : null}
          <DownloadAllButton projectId={projectId} hasReadyStems={readyCount > 0} />
        </div>
      </header>

      {stems.isLoading ? (
        <div className="flex items-center gap-2 text-[var(--text-2)]">
          <Spinner /> Loading stems...
        </div>
      ) : null}

      {stems.error ? (
        <Card>
          <p className="m-0 text-[var(--red)]">
            Couldn't load stems.{' '}
            {stems.error instanceof Error ? stems.error.message : 'Unknown error'}
          </p>
        </Card>
      ) : null}

      {!stems.isLoading && characters.length === 0 ? (
        <EmptyState
          title="No characters yet"
          description="Once characters are recorded, you can render and download their stems here."
        />
      ) : null}

      {characters.length > 0 ? (
        <div className="overflow-x-auto rounded-[var(--radius)] border border-[var(--border)]">
          <table
            className="w-full text-[13px] border-separate"
            style={{ borderSpacing: 0 }}
            role="table"
            aria-label="Stems"
          >
            <thead
              className="sticky top-0 z-10 text-left text-[11px] uppercase tracking-wide text-[var(--text-2)]"
              style={{ background: 'var(--bg-3)' }}
            >
              <tr>
                <Th>Character</Th>
                <Th>Approved</Th>
                <Th>Status</Th>
                <Th>Rendered</Th>
                <Th>Files</Th>
              </tr>
            </thead>
            <tbody>
              {characters.map((c) => (
                <tr
                  key={c.character_id}
                  className="align-middle transition-colors hover:bg-[var(--bg-3)]"
                >
                  <Td>
                    <div className="flex flex-col">
                      <span className="font-medium">{c.character_display_name}</span>
                      <code className="text-[11px] text-[var(--text-3)]">{c.character_code}</code>
                    </div>
                  </Td>
                  <Td>
                    <span className="tabular-nums">
                      {c.approved_takes}/{c.total_cues}
                    </span>
                    {c.pickup_pending > 0 ? (
                      <span className="ml-2">
                        <Badge variant="amber">{c.pickup_pending} pickup</Badge>
                      </span>
                    ) : null}
                  </Td>
                  <Td>
                    <StemRender projectId={projectId} stem={c} />
                  </Td>
                  <Td>
                    {c.rendered_at ? (
                      <span className="text-[12px] text-[var(--text-2)]">
                        {new Date(c.rendered_at).toLocaleString()}
                        {c.rendered_by_email ? <span> - {c.rendered_by_email}</span> : null}
                      </span>
                    ) : (
                      <span className="text-[var(--text-3)]">never</span>
                    )}
                  </Td>
                  <Td>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={!c.download_url}
                        onClick={() => {
                          if (c.download_url) window.open(c.download_url, '_blank', 'noopener')
                        }}
                      >
                        WAV
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={!c.qc_pdf_url}
                        onClick={() => {
                          if (c.qc_pdf_url) window.open(c.qc_pdf_url, '_blank', 'noopener')
                        }}
                      >
                        QC PDF
                      </Button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  )
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-3 py-2 font-medium">{children}</th>
}

function Td({ children }: { children: React.ReactNode }) {
  return (
    <td className="px-3 py-2 border-t" style={{ borderColor: 'var(--border)' }}>
      {children}
    </td>
  )
}
